import { abbreviate } from '@chainflip/utils/string';
import { Link } from '@/shared/components/atoms/Link';
import Button from '@/shared/components/flip-ui-kit/Button';
import CopyButton from '@/shared/components/molecules/CopyButton';
import { FlipLabel } from '@/shared/components/molecules/FlipLabel';
import { TableV2 } from '@/shared/components/molecules/TableV2';
import { TABLE_POLL_INTERVAL } from '@/shared/constants';
import { getHistoricalValidators } from '@/shared/graphql/explorer/delegation';
import { usePendingRedemption } from '@/shared/hooks/funding';
import useAccountInfo from '@/shared/hooks/useAccountInfo';
import { useGqlQuery } from '@/shared/hooks/useGqlQuery';
import { type OperatorValidator } from '@/shared/hooks/useOperator';
import ArrowIcon from '@/shared/icons/flip-ui-kit/small/Arrow';
import MinusCircleIcon from '@/shared/icons/flip-ui-kit/small/MinusCircle';
import { buildChainflipExplorerLink } from '@/shared/utils/chainflip';
import TokenAmount from '@/shared/utils/TokenAmount';

type HistoricalValidator = { idSs58: `cF${string}`; alias: string | null | undefined };

const ValidatorRow = ({
  validator,
  redeemFunds,
}: {
  validator: HistoricalValidator;
  redeemFunds?: (accountId: `cF${string}`) => void;
}) => {
  const { data: redemption = null } = usePendingRedemption(
    redeemFunds ? validator.idSs58 : undefined,
  );
  const { data: accountInfo } = useAccountInfo(validator.idSs58);

  return (
    <tr className="text-12">
      <td>
        <div className="flex flex-col">
          {validator.alias}
          <div className="flex items-center gap-x-1">
            <Link
              href={buildChainflipExplorerLink(validator.idSs58, 'validators')!}
              target={process.env.NEXT_PUBLIC_EXPLORER_URL ? '_blank' : undefined}
              rel="noreferrer"
              underline
              noPropagate
              color="light"
              className="whitespace-nowrap"
            >
              {abbreviate(validator.idSs58, 4)}
            </Link>
            <CopyButton textToCopy={validator.idSs58} />
          </div>
        </div>
      </td>
      <td>
        {accountInfo && (
          <FlipLabel
            amount={TokenAmount.fromAsset(accountInfo.flip_balance, 'Flip')}
            className="font-aeonikMedium text-12"
          />
        )}
      </td>
      <td>
        {accountInfo && (
          <FlipLabel
            amount={TokenAmount.fromAsset(accountInfo.estimated_redeemable_balance, 'Flip')}
            className="font-aeonikMedium text-12"
          />
        )}
      </td>
      {redeemFunds && (
        <td>
          <div className="flex justify-end gap-x-2">
            <Button
              className="whitespace-nowrap"
              icon={
                redemption ? (
                  <ArrowIcon className="ml-1 text-cf-orange-1" direction="right" />
                ) : (
                  <MinusCircleIcon className="mr-1 group-enabled:text-cf-red-2" />
                )
              }
              type="secondary-standard"
              iconPos={redemption ? 'right' : 'left'}
              size="small"
              onClick={() => redeemFunds(validator.idSs58)}
              disabled={
                !redemption &&
                (accountInfo === undefined || accountInfo.estimated_redeemable_balance === 0n)
              }
            >
              {redemption ? 'Continue' : 'Redeem'}
            </Button>
          </div>
        </td>
      )}
    </tr>
  );
};

export type PastValidatorsTableProps = {
  idSs58: `cF${string}`;
  redeemFunds?: (accountId: `cF${string}`) => void;
  currentValidators: OperatorValidator[];
};

export const PastValidatorsTable = ({
  idSs58,
  redeemFunds,
  currentValidators,
}: PastValidatorsTableProps) => {
  const { isLoading: historicalValidatorsLoading, data: historicalValidatorsData } = useGqlQuery(
    getHistoricalValidators,
    {
      refetchInterval: TABLE_POLL_INTERVAL,
      variables: { idSs58 },
      context: { clientName: 'processor' },
    },
  );

  const filteredValidators =
    historicalValidatorsData?.links?.nodes
      .filter(
        ({ validator }) => !currentValidators.some((cv) => cv.idSs58 === validator.account.idSs58),
      )
      // unique by idSs58
      .filter(
        ({ validator }, index, array) =>
          array.findLastIndex((v) => v.validator.account.idSs58 === validator.account.idSs58) ===
          index,
      ) ?? [];

  return (
    <TableV2
      isLoading={historicalValidatorsLoading}
      columns={[
        'Validator ID',
        'Balance',
        'Redeemable',
        ...(redeemFunds
          ? [
              {
                name: 'Actions',
                labelHidden: true,
                alignment: 'right',
              } as const,
            ]
          : []),
      ]}
      className="bg-cf-gray-3"
      rows={
        filteredValidators.map(({ validator }) => (
          <ValidatorRow
            key={validator.account.idSs58}
            redeemFunds={redeemFunds}
            validator={{
              idSs58: validator.account.idSs58 as `cF${string}`,
              alias: validator.account.alias,
            }}
          />
        )) ?? []
      }
    />
  );
};
